import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Share2, Twitter, Facebook, Copy, Check } from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import {
  MarketplaceExternalStoreLink,
  MarketplaceFooter,
  MarketplaceHeader,
  MarketplaceSectionHeading,
  ProductCard,
  getMarketplacePath,
  getStorefrontLabel,
  getStorefrontUrl,
  marketplaceCurrency,
  trackSponsoredProductView
} from './MarketplaceShared';
import MarketplaceSeo, { marketplaceUrlFor } from './MarketplaceSeo';
import './Marketplace.css';

// WhatsApp brand mark — lucide only ships MessageCircle which reads as
// a generic chat bubble, not the recognisable WhatsApp logo.
const WhatsAppIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .103 5.334.1 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.9 11.9 0 0 0 5.71 1.447h.005c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411l.016-.039zm-8.475 18.297h-.004a9.87 9.87 0 0 1-5.031-1.378l-.36-.214-3.741.98.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.455-9.882 9.885-9.882 2.635 0 5.115 1.033 6.978 2.907 1.86 1.87 2.884 4.352 2.882 6.984 0 5.45-4.455 9.882-9.867 9.882l.005.003z" />
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347z" />
  </svg>
);

// Compact share widget: a circular trigger that sits inline with the
// primary CTAs. On desktop, hovering the wrapper reveals the icons; on
// touch/mobile a tap toggles them. The panel is absolutely positioned
// so it never pushes surrounding layout — it grows leftward on desktop
// and drops below-right on mobile so it can't clamp against edges.
function MarketplaceShareMenu({ url, title }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const encodedUrl = encodeURIComponent(url || '');
  const encodedText = encodeURIComponent(title ? `${title} — ${url || ''}` : (url || ''));
  const whatsappUrl = `https://wa.me/?text=${encodedText}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title || '')}&url=${encodedUrl}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url || '');
      } else {
        // Older browser fallback
        const textarea = document.createElement('textarea');
        textarea.value = url || '';
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      // silently fall through — user can still use the other channels
    }
  };

  return (
    <div
      className="marketplace-share"
      ref={wrapperRef}
      data-open={open ? 'true' : undefined}
    >
      <button
        type="button"
        className="marketplace-share-trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Share this product"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Share2 size={16} />
      </button>
      <div className="marketplace-share-panel" role="menu" aria-label="Share options">
        <a
          className="marketplace-share-item"
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Share on WhatsApp"
          onClick={() => setOpen(false)}
          role="menuitem"
        >
          <WhatsAppIcon size={16} />
        </a>
        <a
          className="marketplace-share-item"
          href={twitterUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Share on X"
          onClick={() => setOpen(false)}
          role="menuitem"
        >
          <Twitter size={16} />
        </a>
        <a
          className="marketplace-share-item"
          href={facebookUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="Share on Facebook"
          onClick={() => setOpen(false)}
          role="menuitem"
        >
          <Facebook size={16} />
        </a>
        <button
          type="button"
          className="marketplace-share-item"
          onClick={handleCopy}
          aria-label={copied ? 'Link copied' : 'Copy product link'}
          role="menuitem"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
    </div>
  );
}

function MarketplaceProductPage() {
  const { storeSlug, productSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState('');

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const campaignId = searchParams.get('campaign');
        const { data } = await axiosInstance.get(`/marketplace/products/${storeSlug}/${productSlug}`, {
          params: campaignId ? { campaign: campaignId } : {}
        });
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load product');
        }
        if (alive) {
          setPayload(data);
          setActiveImage(data.product?.image || data.product?.gallery?.[0] || '');
        }
      } catch (err) {
        if (alive) setError(err?.response?.data?.message || err.message || 'Failed to load product');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [storeSlug, productSlug, searchParams]);

  useEffect(() => {
    const campaignId = payload?.product?.sponsorship?.campaignId;
    if (campaignId) trackSponsoredProductView(campaignId);
  }, [payload]);

  const gallery = useMemo(() => {
    const product = payload?.product;
    const images = [product?.image, ...(product?.gallery || [])].filter(Boolean);
    return Array.from(new Set(images));
  }, [payload]);

  if (loading) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="home" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card marketplace-loading-card">
            <h3>Loading product</h3>
            <p>Pulling product details and merchant context.</p>
          </section>
        </div>
      </div>
    );
  }

  if (error || !payload?.product) {
    return (
      <div className="marketplace-page">
        <MarketplaceHeader active="home" />
        <div className="marketplace-shell marketplace-shell--centered">
          <section className="marketplace-feedback-card">
            <h3>Product not available</h3>
            <p>{error || 'The selected product could not be found.'}</p>
          </section>
        </div>
      </div>
    );
  }

  const { product, merchant, relatedProducts } = payload;
  const buyIntentParam = product.hasVariations ? '' : '?buy=now&source=marketplace';
  const productUrl = `${getStorefrontUrl(merchant)}/product/${product.slug}${buyIntentParam}`;
  const shopUrl = `${getStorefrontUrl(merchant)}/shop`;
  const compareVisible = Number(product.compareAtPrice || 0) > Number(product.price || 0);

  const marketplaceProductUrl = marketplaceUrlFor(`/product/${merchant.slug}/${product.slug}`);
  const productSeoTitle = product.name;
  const productSeoDescription = product.shortDescription
    || product.description
    || `${product.name} — available from ${merchant.name} on the Revstar marketplace.`;
  const productAvailable = Number(product.quantity || 0) > 0
    || product.productType !== 'PHYSICAL';
  const productJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': marketplaceProductUrl,
      name: product.name,
      description: product.description || product.shortDescription || undefined,
      image: [product.image, ...(product.gallery || [])].filter(Boolean),
      sku: product.id,
      brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
      category: product.category || undefined,
      offers: {
        '@type': 'Offer',
        url: marketplaceProductUrl,
        priceCurrency: product.currency || 'NGN',
        price: Number(product.price || 0),
        availability: productAvailable
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: merchant.name,
          url: marketplaceUrlFor(`/merchant/${merchant.slug}`)
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Marketplace', item: marketplaceUrlFor('/') },
        { '@type': 'ListItem', position: 2, name: 'Stores', item: marketplaceUrlFor('/merchants') },
        { '@type': 'ListItem', position: 3, name: merchant.name, item: marketplaceUrlFor(`/merchant/${merchant.slug}`) },
        { '@type': 'ListItem', position: 4, name: product.name, item: marketplaceProductUrl }
      ]
    }
  ];

  return (
    <div className="marketplace-page">
      <MarketplaceSeo
        title={productSeoTitle}
        description={productSeoDescription}
        path={`/product/${merchant.slug}/${product.slug}`}
        image={product.image || undefined}
        type="product"
        jsonLd={productJsonLd}
      />
      <MarketplaceHeader active="home" />

      <div className="marketplace-shell marketplace-shell--spacious">
        <section className="marketplace-breadcrumbs">
          <Link to={getMarketplacePath('/')}>Marketplace</Link>
          <span>/</span>
          <Link to={getMarketplacePath('/merchants')}>Stores</Link>
          <span>/</span>
          <Link to={getMarketplacePath(`/merchant/${merchant.slug}`)}>{merchant.name}</Link>
          <span>/</span>
          <span>{product.name}</span>
        </section>

        <section className="marketplace-product-layout">
          <div className="marketplace-product-gallery">
            <div className="marketplace-product-main-media">
              {activeImage ? <img src={activeImage} alt={product.name} /> : <div className="marketplace-image-fallback" />}
            </div>

            {gallery.length > 1 ? (
              <div className="marketplace-product-thumbs">
                {gallery.map((image) => (
                  <button
                    key={image}
                    type="button"
                    className={`marketplace-product-thumb${activeImage === image ? ' is-active' : ''}`}
                    onClick={() => setActiveImage(image)}
                  >
                    <img src={image} alt={product.name} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="marketplace-product-panel">
            {product.sponsorship ? <span className="marketplace-sponsored-badge">{product.sponsorship.label}</span> : null}
            <h1>{product.name}</h1>
            <p className="marketplace-product-domain">{getStorefrontLabel(merchant)}</p>

            <div className="marketplace-product-price">
              <strong>{marketplaceCurrency(product.price, product.currency)}</strong>
              {compareVisible ? <s>{marketplaceCurrency(product.compareAtPrice, product.currency)}</s> : null}
            </div>

            <p className="marketplace-product-description">
              {product.shortDescription || product.description || 'Marketplace previews the product here. Continue inside the merchant storefront to add it to cart and complete payment.'}
            </p>

            <div className="marketplace-product-tags">
              <span>{product.category || 'General'}</span>
              <span>{product.productType || 'PHYSICAL'}</span>
              <span>
                {product.hasVariations
                  ? (product.quantity > 0 ? 'Variants available' : 'Variants out of stock')
                  : (product.quantity > 0 ? `${product.quantity} in stock` : 'Out of stock')}
              </span>
              {product.brand ? <span>{product.brand}</span> : null}
            </div>

            <div className="marketplace-product-actions">
              <a className="marketplace-primary-btn" href={productUrl} target="_blank" rel="noreferrer">
                View on merchant store
              </a>
              <a className="marketplace-subtle-link" href={shopUrl} target="_blank" rel="noreferrer">
                Visit storefront
              </a>
              <MarketplaceShareMenu url={marketplaceProductUrl} title={product.name} />
            </div>

            <div className="marketplace-product-merchant-card">
              <div>
                <span>Sold by</span>
                <strong>{merchant.name}</strong>
                <p>{getStorefrontLabel(merchant)}</p>
              </div>
              <MarketplaceExternalStoreLink store={merchant}>View store</MarketplaceExternalStoreLink>
            </div>
          </div>
        </section>

        {relatedProducts.length ? (
          <section className="marketplace-section">
            <MarketplaceSectionHeading
              eyebrow="More from this merchant"
              title="Keep browsing before you enter the live store"
            />
            <div className="marketplace-product-grid">
              {relatedProducts.map((item) => (
                <ProductCard key={item.id} product={item} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
      <MarketplaceFooter />
    </div>
  );
}

export default MarketplaceProductPage;
